import React, { useEffect, useState } from 'react';
import { Spin, message } from 'antd';
import { LeftOutlined, RightOutlined, DownOutlined, DownloadOutlined } from '@ant-design/icons';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import withStyles from 'react-jss';
import { formatNumber, transformName } from 'src/utils/utils';
import { downloadRequestRoute } from 'src/routing/routeConstants';
import { fetchPayoutDetails, fetchPayoutCharges, downloadPayoutExcel } from '../Receivables.api';
import { renderAttachmentLink } from '../PDFViewComponent';
import classNames from 'classnames';

interface BucketColumn {
  column_id: string;
  pretty_name: string;
  type?: 'text' | 'number' | 'attachment' | 'action';
  is_expandable?: boolean;
  group_value_key?: string;
  width?: number;
  fixed?: string;
  bucket_list?: string[];
  [key: string]: any;
}

interface PayoutDetailsTableProps {
  calculationData?: {
    id?: string;
    entity_type?: string;
    entity_reference_number?: string;
    status?: string;
    start_date?: string;
    end_date?: string;
    hub_code?: string;
    [key: string]: string | number | boolean;
  };
  currencySymbol?: string;
  reviewScreenColumns?: BucketColumn[];
  activeViewType?: string;
  classes?: any;
}

const styles = {
  payoutDetailsTable: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    position: 'relative',
  },
  tableWrapper: {
    flex: 1,
    overflowY: 'auto',
    overflowX: 'auto',
    border: '0.5px solid #ddd',
  },
  tableContainer: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  tableHeader: {
    display: 'flex',
    backgroundColor: '#f2f7fc',
    borderBottom: '0.5px solid #ddd',
    position: 'sticky',
    top: 0,
    zIndex: 10,
  },
  tableHeaderCell: {
    padding: '15px 16px',
    fontSize: '12px',
    fontWeight: 400,
    color: '#333',
    borderRight: '0.5px solid #ddd',
    height: '36px',
    display: 'flex',
    alignItems: 'center',
    '&:last-child': {
      borderRight: 'none',
    },
  },
  tableRow: {
    display: 'flex',
    backgroundColor: '#FFFFFF',
    borderBottom: '0.5px solid #ddd',
    '&:last-child': {
      borderBottom: 'none',
    },
    '&:hover': {
      backgroundColor: '#fafafa',
    },
  },
  tableCell: {
    padding: '15px 16px',
    fontSize: '12px',
    borderRight: '0.5px solid #ddd',
    height: '48px',
    display: 'flex',
    alignItems: 'center',
    '&:last-child': {
      borderRight: 'none',
    },
  },
  expandedContent: {
    width: '100%',
    backgroundColor: '#FFFFFF',
    borderBottom: '0.5px solid #ddd',
  },
  nestedTable: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  nestedTableRow: {
    display: 'flex',
    backgroundColor: '#FFFFFF',
    borderBottom: '0.5px solid #ddd',
    '&:hover': {
      backgroundColor: '#fafafa',
    },
  },
  totalSection: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 16px',
    borderTop: '1px solid #DDDDDD',
    background: '#d5f3d7',
    width: '100%',
    position: 'sticky',
    bottom: 0,
    zIndex: 10,
  },
  summaryRow: {
    display: 'flex',
    backgroundColor: '#d5f3d7',
    borderTop: '1px solid #DDDDDD',
    position: 'sticky',
    bottom: 0,
    zIndex: 10,
    width: '100%',
  },
  summaryCell: {
    padding: '15px 16px',
    fontSize: '12px',
    fontWeight: 600,
    height: '48px',
    display: 'flex',
    alignItems: 'center',
    color: '#111111',
    '&:last-child': {
      borderRight: 'none',
    },
  },
  totalLabel: {
    fontSize: '12px',
    fontWeight: 600,
    color: '#111111',
  },
  totalValue: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#111111',
    marginLeft: '450px',
  },
  dynamicCol: {
    flex: 1,
    minWidth: '100px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  chargeNameText: {
    fontWeight: 500,
    fontSize: '12px',
    color: '#111',
  },
  entityNumberText: {
    fontWeight: 500,
    fontSize: '12px',
    color: '#111',
  },
  entityTypeText: {
    fontSize: '10px',
    color: '#666',
    marginTop: '4px',
  },
  amountText: {
    fontWeight: 500,
    fontSize: '12px',
    color: '#111',
  },
  remarksText: {
    fontWeight: 500,
    fontSize: '12px',
    color: '#111',
  },
  emptyText: {
    fontSize: '12px',
    color: '#111',
  },
  expandIcon: {
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    fontSize: '12px',
  },
  expandContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  loadingContainer: {
    padding: '20px',
    textAlign: 'center',
  },
  noDataContainer: {
    padding: '20px',
    textAlign: 'center',
    color: '#666',
  },
  paginationContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
  },
  paginationButton: {
    border: 'none',
    background: 'transparent',
    padding: '4px',
    display: 'flex',
    alignItems: 'center',
    '&:disabled': {
      cursor: 'not-allowed',
      opacity: 0.3,
    },
    '&:not(:disabled)': {
      cursor: 'pointer',
      opacity: 1,
    },
  },
  paginationIcon: {
    fontSize: '12px',
    color: '#666666',
  },
  paginationText: {
    fontSize: '12px',
    color: '#666666',
    minWidth: '40px',
    textAlign: 'center',
  },
  loadingSpinner: {
    padding: '40px',
    textAlign: 'center',
  },
  toolbarContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    paddingBottom: '8px',
    backgroundColor: '#FFFFFF',
    borderBottom: '0.5px solid #ddd',
    gap: '12px',
  },
  downloadButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '6px 12px',
    border: '1px solid #1659CB',
    borderRadius: '4px',
    backgroundColor: '#FFFFFF',
    cursor: 'pointer',
    background:
      'linear-gradient(90deg, rgba(22, 89, 203, 0.03) 0%, rgba(22, 89, 203, 0.03) 100%), linear-gradient(90deg, rgb(255, 255, 255) 0%, rgb(255, 255, 255) 100%)',
    height: '28px',
    '&:hover': {
      backgroundColor: '#f0f7ff',
    },
  },
  downloadIcon: {
    fontSize: '14px',
    color: '#1659CB',
  },
};

interface ChargeDetail {
  charge_id?: string;
  charge_name?: string;
  entity_reference_number?: string;
  entity_type?: string;
  amount?: number;
  attachment?: string;
  remarks?: string;
  group_key?: string;
  header?: string;
  total_amount?: number;
  total_count?: number;
  [key: string]: any;
}

const getColumnStyle = (col: BucketColumn): React.CSSProperties => {
  const style: React.CSSProperties = {};
  if (col.width) {
    style.flex = `0 0 ${col.width}px`;
    style.width = col.width;
    style.minWidth = col.width;
  } else {
    style.flex = 1;
    style.minWidth = 100;
  }
  if (col.type === 'number') {
    style.textAlign = 'right';
    style.justifyContent = 'flex-end';
  }
  return style;
};

const PayoutDetailsTable: React.FC<PayoutDetailsTableProps> = ({
  calculationData = {},
  currencySymbol = '',
  reviewScreenColumns = [],
  activeViewType,
  classes,
}) => {
  const { t } = useTranslation();
  const history = useHistory();

  const columns = reviewScreenColumns || [];

  const payoutId = String(calculationData.id || '');
  const [chargeDetails, setChargeDetails] = useState<ChargeDetail[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [expandedRowKey, setExpandedRowKey] = useState<React.Key | null>(null);
  const [nestedChargesData, setNestedChargesData] = useState<{ [key: string]: ChargeDetail[] }>({});
  const [loadingNestedCharges, setLoadingNestedCharges] = useState<{ [key: string]: boolean }>({});
  const [pagination, setPagination] = useState<{ [key: string]: number }>({});

  useEffect(() => {
    const loadPayoutDetails = async () => {
      if (!payoutId) return;

      setLoading(true);
      setExpandedRowKey(null);
      setNestedChargesData({});
      setLoadingNestedCharges({});
      setPagination({});

      try {
        const response = await fetchPayoutDetails(payoutId, activeViewType);
        if (response.isSuccess && response.data) {
          const data = Array.isArray(response.data) ? response.data : [];
          setChargeDetails(data);
        } else {
          message.error(response.errorMessage || 'Failed to fetch payout details');
        }
      } catch (error) {
        message.error('An error occurred while fetching payout details');
      } finally {
        setLoading(false);
      }
    };

    loadPayoutDetails();
  }, [payoutId]);

  const formatChargeName = (record: ChargeDetail) => transformName(record.header || record.group_key || record.charge_name || record.charge_id || '');

  // Fetch nested charges for a specific charge
  const fetchNestedCharges = async (groupKey: string) => {
    if (!payoutId || !groupKey) return;

    setLoadingNestedCharges((prev) => ({ ...prev, [groupKey]: true }));
    try {
      const response = await fetchPayoutCharges(payoutId, groupKey, activeViewType);
      if (response.isSuccess && response.data) {
        const data = Array.isArray(response.data) ? response.data : [];
        setNestedChargesData((prev) => ({ ...prev, [groupKey]: data }));
      } else {
        message.error(response.errorMessage || 'Failed to fetch nested charges');
      }
    } catch (error) {
      message.error('An error occurred while fetching nested charges');
    } finally {
      setLoadingNestedCharges((prev) => ({ ...prev, [groupKey]: false }));
    }
  };

  // Handle row expand/collapse - only one row can be expanded at a time
  const handleExpand = (expanded: boolean, record: ChargeDetail) => {
    const groupKey = record.group_key || record.charge_id || record.charge_name || '';
    const rowKey = record.header || record.charge_id || record.entity_reference_number || groupKey;

    if (expanded) {
      // Close any previously expanded row
      setExpandedRowKey(rowKey);
      // Initialize pagination for this row
      setPagination((prev) => ({ ...prev, [rowKey]: 1 }));
      // Fetch nested charges if not already loaded
      if (!nestedChargesData[groupKey]) {
        fetchNestedCharges(groupKey);
      }
    } else {
      setExpandedRowKey(null);
      // Reset pagination when collapsed
      setPagination((prev) => {
        const newPagination = { ...prev };
        delete newPagination[rowKey];
        return newPagination;
      });
    }
  };

  // Handle pagination change
  const handlePageChange = (rowKey: React.Key, direction: 'prev' | 'next', totalPages: number) => {
    const currentPage = pagination[rowKey] || 1;
    let newPage = currentPage;

    if (direction === 'prev' && currentPage > 1) {
      newPage = currentPage - 1;
    } else if (direction === 'next' && currentPage < totalPages) {
      newPage = currentPage + 1;
    }

    setPagination((prev) => ({ ...prev, [rowKey]: newPage }));
  };

  // Render pagination controls
  const renderPagination = (
    rowKey: React.Key,
    record: ChargeDetail,
  ) => {
    const groupKey =
      record.group_key || record.charge_id || record.charge_name || '';
    const nestedCharges = nestedChargesData[groupKey] || [];
    const currentPage = pagination[rowKey] || 1;
    const pageSize = 5;
    const totalPages = Math.ceil(nestedCharges.length / pageSize);

    if (!expandedRowKey || expandedRowKey !== rowKey || nestedCharges.length === 0) {
      return null;
    }

    return (
      <div className={classes.paginationContainer}>
        <button
          type="button"
          onClick={() => handlePageChange(rowKey, 'prev', totalPages)}
          disabled={currentPage <= 1}
          className={classes.paginationButton}
        >
          <LeftOutlined className={classes.paginationIcon} />
        </button>
        <span className={classes.paginationText}>
          {currentPage} / {totalPages}
        </span>
        <button
          type="button"
          onClick={() => handlePageChange(rowKey, 'next', totalPages)}
          disabled={currentPage >= totalPages}
          className={classes.paginationButton}
        >
          <RightOutlined className={classes.paginationIcon} />
        </button>
      </div>
    );
  };

  // Generic cell renderer driven by column type
  const renderCellContent = (
    col: BucketColumn,
    record: ChargeDetail,
    opts?: {
      isExpanded?: boolean;
      canExpand?: boolean;
      rowKey?: React.Key;
      isNested?: boolean;
    },
  ) => {
    const { isExpanded, canExpand, rowKey, isNested } = opts || {};
    const value =
      record.group_key && col.group_value_key && !isNested
        ? record[col.group_value_key]
        : record[col.column_id];

    // Expandable column: wrap with expand/collapse arrow
    if (col.is_expandable && !isNested) {
      return (
        <span className={classes.expandContainer}>
          {canExpand && (
            <span
              onClick={(e) => {
                e.stopPropagation();
                handleExpand(!isExpanded, record);
              }}
              className={classes.expandIcon}
            >
              {isExpanded ? <DownOutlined /> : <RightOutlined />}
            </span>
          )}
          <span className={classes.chargeNameText}>
            {formatChargeName(record)}
          </span>
        </span>
      );
    }

    switch (col.type) {
      case 'number':
        return (
          <span className={classes.amountText}>
            {formatNumber(value || 0)}
          </span>
        );
      case 'attachment':
        return value
          ? renderAttachmentLink(value)
          : <span className={classes.emptyText} />;
      case 'action':
        if (!isNested && rowKey) {
          return renderPagination(rowKey, record);
        }
        return <span className={classes.emptyText} />;
      default:
        return (
          <span className={classes.emptyText}>
            {value ?? ''}
          </span>
        );
    }
  };

  // Render nested table row
  const renderNestedRow = (record: ChargeDetail) => {
    const rowKey =
      record.charge_id || record.entity_reference_number || Math.random();
    return (
      <div className={classes.nestedTableRow} key={rowKey}>
        {columns.map((col, colIdx) => (
          <div
            key={col.column_id}
            className={classNames(classes.tableCell, classes.dynamicCol)}
            style={getColumnStyle(col)}
          >
            {colIdx === 0 ? (
              <span style={{ paddingLeft: 24 }}>
                {renderCellContent(col, record, { isNested: true })}
              </span>
            ) : (
              renderCellContent(col, record, { isNested: true })
            )}
          </div>
        ))}
      </div>
    );
  };

  // Render nested table with pagination
  const renderNestedTable = (
    record: ChargeDetail,
    rowKey: React.Key,
  ) => {
    const groupKey =
      record.group_key || record.charge_id || record.charge_name || '';
    const nestedCharges = nestedChargesData[groupKey] || [];
    const isLoading = loadingNestedCharges[groupKey];
    const currentPage = pagination[rowKey] || 1;
    const pageSize = 5;
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedCharges = nestedCharges.slice(startIndex, endIndex);

    if (isLoading) {
      return (
        <div className={classes.loadingContainer}>
          <Spin size="small" />
        </div>
      );
    }

    if (!nestedCharges?.length) {
      return (
        <div className={classes.noDataContainer}>
          No nested charges found
        </div>
      );
    }

    return (
      <div className={classes.nestedTable}>
        {paginatedCharges.map((nestedRecord) => renderNestedRow(nestedRecord))}
      </div>
    );
  };

  // Calculate total from chargeDetails
  const totalAmount = React.useMemo(() => {
    return chargeDetails.reduce((acc, curr) => {
      const amount = curr.group_key ? curr.total_amount : curr.amount;
      return acc + (Number(amount) || 0);
    }, 0);
  }, [chargeDetails]);

  const handleDownload = async () => {
    const requestData = {
      object_type: 'vendor',
      entity_type_list: calculationData?.entity_type ? [calculationData.entity_type] : undefined,
      entity_reference_number_list: calculationData?.entity_reference_number
        ? [calculationData.entity_reference_number]
        : undefined,
      status_list: calculationData?.status ? [calculationData.status] : [],
      start_date: calculationData?.start_date,
      end_date: calculationData?.end_date,
      date_type: 'payout_cycle',
      planning_hub_id_list: calculationData?.hub_code ? [calculationData.hub_code] : [],
      payout_id: payoutId,
    };

    const hideLoading = message.loading('Initiating download request...', 0);
    try {
      const response = await downloadPayoutExcel(requestData, activeViewType);
      hideLoading();

      if (response.isSuccess) {
        message.success('Download request submitted successfully');
        history.push(`${downloadRequestRoute}?dumpType=VENDOR_PAYOUT`);
      } else {
        message.error(response.errorMessage || 'Failed to submit download request');
      }
    } catch (error) {
      hideLoading();
      message.error('An error occurred while submitting download request');
    }
  };

  if (loading) {
    return (
      <div className={classes.loadingSpinner}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className={classes.payoutDetailsTable}>
      {/* Toolbar with Download Button */}
      <div className={classes.toolbarContainer}>
        <button
          type="button"
          onClick={handleDownload}
          className={classes.downloadButton}
        >
          <DownloadOutlined className={classes.downloadIcon} />
        </button>
      </div>

      <div className={classes.tableWrapper}>
        <div className={classes.tableContainer}>
          {/* Table Header */}
          <div className={classes.tableHeader}>
            {columns.map((col) => (
              <div
                key={col.column_id}
                className={classNames(classes.tableHeaderCell, classes.dynamicCol)}
                style={getColumnStyle(col)}
              >
                {col.pretty_name}
              </div>
            ))}
          </div>

          {/* Table Rows */}
          {chargeDetails.map((record, index) => {
            const rowKey = record.charge_id || record.entity_reference_number || record.header || record.group_key || `row-${index}`;
            const isExpanded = expandedRowKey === rowKey;
            const canExpand = !!record.group_key;

            return (
              <React.Fragment key={rowKey}>
                <div className={classes.tableRow}>
                  {columns.map((col) => (
                    <div
                      key={col.column_id}
                      className={classNames(classes.tableCell, classes.dynamicCol)}
                      style={getColumnStyle(col)}
                    >
                      {renderCellContent(col, record, { isExpanded, canExpand, rowKey })}
                    </div>
                  ))}
                </div>

                {/* Expanded Nested Table */}
                {isExpanded && (
                  <div className={classes.expandedContent}>
                    {renderNestedTable(record, rowKey)}
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Summary Row - Sticky at bottom */}
      <div className={classes.summaryRow}>
        {columns.map((col, colIdx) => {
          let content: React.ReactNode = null;
          if (colIdx === 0) {
            content = <strong>Total</strong>;
          } else if (col.type === 'number') {
            content = (
              <strong>
                {totalAmount !== undefined && totalAmount !== null
                  ? `${currencySymbol}${formatNumber(totalAmount)}`
                  : '-'}
              </strong>
            );
          }
          return (
            <div
              key={col.column_id}
              className={classNames(classes.summaryCell, classes.dynamicCol)}
              style={getColumnStyle(col)}
            >
              {content}
            </div>
          );
        })}
      </div>
    </div>
  );
};
const mapStateToProps = ({ masterData, receivables }) => ({
  currencySymbol: masterData?.currency_symbol || '',
  reviewScreenColumns: receivables?.payoutBucketColumns?.review_screen || [],
  activeViewType: receivables?.activeViewType,
});

const StyledPayoutDetailsTable = withStyles(styles, { injectTheme: true })(PayoutDetailsTable);
export default connect(mapStateToProps)(StyledPayoutDetailsTable);
